import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import PaypalButton from "../paypal";
import { formatCount } from "../Home/useAnimatedCount";

const TokenPaymentPanel = ({
  selectedProduct,
  totalSelectedContainedQty,
  totalAmountWithoutTax,
  productTax,
  total,
  taxRate,
  paypalResponse,
  setPaypalResponse,
  loading,
  paymentQty,
  tokensLabel,
  paymentDisabled,
  paymentDisabledMessage,
  validityChecking
}) => {
  const { t } = useTranslation("common");

  const planName = selectedProduct?.product_name || selectedProduct?.name || "—";
  const paypalQty =
    paymentQty != null ? Number(paymentQty) : Number(totalSelectedContainedQty);
  const canPay =
    selectedProduct?.id &&
    paypalQty > 0 &&
    Number(total) > 0 &&
    !paymentDisabled &&
    !validityChecking;

  const emptyPaymentMessage = () => {
    if (validityChecking) {
      return t("Checking subscription status...");
    }
    if (paymentDisabled && paymentDisabledMessage) {
      return paymentDisabledMessage;
    }
    if (paymentDisabled) {
      return t("Payment is unavailable until your subscription status is verified.");
    }
    if (loading) {
      return t("Fetching...");
    }
    return t("Loading payment options...");
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="card-title">{t("Payment")}</div>
        </div>
        <div className="price-summary">
          <div className="price-row">
            <span>{t("Plan")}</span>
            <span>{planName}</span>
          </div>
          <div className="price-row">
            <span>{tokensLabel || t("Tokens")}</span>
            <span className="val-green">+{formatCount(totalSelectedContainedQty)}</span>
          </div>
          <div className="price-row">
            <span>{t("Subtotal")}</span>
            <span>${totalAmountWithoutTax}</span>
          </div>
          <div className="price-row">
            <span>{t("Tax ({{rate}}%)", { rate: taxRate })}</span>
            <span>${productTax}</span>
          </div>
          <div className="price-row total">
            <span>{t("Total")}</span>
            <span>${total}</span>
          </div>
        </div>

        <div className="paypal-checkout-wrap">
          {canPay ? (
            <PayPalScriptProvider
              options={{
                "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                currency: "USD",
                intent: "capture"
              }}>
              <PaypalButton
                productId={selectedProduct.id}
                productQty={paypalQty}
                productprice={total}
                paypalResponse={paypalResponse}
                setPaypalResponse={setPaypalResponse}
              />
            </PayPalScriptProvider>
          ) : (
            <div
              className={`dashboard-empty${paymentDisabled && !validityChecking ? " payment-disabled" : ""}`}
              style={{ padding: "12px 0" }}>
              {emptyPaymentMessage()}
            </div>
          )}
        </div>

        <div className="token-ssl-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>{t("Secured with 256-bit SSL encryption")}</span>
        </div>
      </div>
    </>
  );
};

TokenPaymentPanel.propTypes = {
  selectedProduct: PropTypes.object,
  totalSelectedContainedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  totalAmountWithoutTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  productTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  taxRate: PropTypes.number,
  paypalResponse: PropTypes.object,
  setPaypalResponse: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  paymentQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tokensLabel: PropTypes.string,
  paymentDisabled: PropTypes.bool,
  paymentDisabledMessage: PropTypes.string,
  validityChecking: PropTypes.bool
};

TokenPaymentPanel.defaultProps = {
  selectedProduct: null,
  taxRate: 5,
  paypalResponse: null,
  loading: false,
  paymentQty: null,
  tokensLabel: null,
  paymentDisabled: false,
  paymentDisabledMessage: "",
  validityChecking: false
};

export default TokenPaymentPanel;
